import { Archive, ArchiveRestore, Loader2, Pencil, Plus } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { useResource, useResourceMutations, type Meta } from '@/lib/settings'
import { cn } from '@/lib/utils'
import { SettingsCard } from './form-kit'

/**
 * Manage a simple configurable list (VAT rates, units, categories, contract
 * types, starter pack items): add, edit, archive, restore. Archived rows stay
 * available to old quotes but cannot be chosen for new ones.
 */
export function ResourceManager<T extends Meta>({
  path,
  noun,
  title,
  description,
  canEdit,
  empty,
  row,
  form,
  toInput,
  blank,
  archiveBlocked,
}: {
  path: string
  noun: string
  title: string
  description?: ReactNode
  canEdit: boolean
  empty: string
  row: (item: T) => { primary: ReactNode; secondary?: ReactNode; badges?: ReactNode }
  /** Renders the fields for the draft; return an error map for validation. */
  form: (
    draft: Partial<T>,
    set: (patch: Partial<T>) => void,
  ) => { fields: ReactNode; errors: Record<string, string> }
  toInput: (draft: Partial<T>) => Partial<T>
  blank: Partial<T>
  archiveBlocked?: (item: T) => string | null
}) {
  const { data, isPending, isError, error } = useResource<T>(path)
  const { create, update } = useResourceMutations<T>(path, noun)
  const [showArchived, setShowArchived] = useState(false)
  const [editing, setEditing] = useState<{ item: T | null; draft: Partial<T> } | null>(null)

  const items = (data ?? []).filter((i) => showArchived || i.active)
  const archivedCount = (data ?? []).filter((i) => !i.active).length
  const rendered = editing
    ? form(editing.draft, (patch) =>
        setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e)),
      )
    : null
  const hasErrors = rendered ? Object.keys(rendered.errors).length > 0 : false
  const saving = create.isPending || update.isPending

  function submit() {
    if (!editing || hasErrors) return
    const body = toInput(editing.draft)
    const done = { onSuccess: () => setEditing(null) }
    if (editing.item)
      update.mutate(
        { ...body, id: editing.item.id, expectedVersion: editing.item.version } as never,
        done,
      )
    else create.mutate(body, done)
  }

  return (
    <SettingsCard
      title={title}
      description={description}
      action={
        canEdit && (
          <Button className="h-10" onClick={() => setEditing({ item: null, draft: { ...blank } })}>
            <Plus />
            Add {noun}
          </Button>
        )
      }
    >
      {isError && (
        <p role="alert" className="text-destructive">
          {error.message}
        </p>
      )}
      {isPending ? (
        <div className="grid gap-2">
          <Skeleton className="h-14" />
          <Skeleton className="h-14" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-[15px] text-muted-foreground">{empty}</p>
      ) : (
        <ul className="-my-2 divide-y">
          {items.map((item) => {
            const r = row(item)
            const blocked = archiveBlocked?.(item) ?? null
            return (
              <li
                key={item.id}
                className={cn(
                  'flex flex-wrap items-center gap-3 py-3',
                  !item.active && 'opacity-60',
                )}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2 font-medium">
                    {r.primary}
                    {r.badges}
                    {!item.active && <Badge variant="outline">Archived</Badge>}
                  </div>
                  {r.secondary && (
                    <div className="mt-0.5 text-sm text-muted-foreground">{r.secondary}</div>
                  )}
                </div>
                {canEdit && (
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon-lg"
                      aria-label={`Edit ${noun}`}
                      onClick={() => setEditing({ item, draft: { ...item } })}
                    >
                      <Pencil />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon-lg"
                      aria-label={item.active ? `Archive ${noun}` : `Restore ${noun}`}
                      title={item.active && blocked ? blocked : undefined}
                      disabled={saving || (item.active && !!blocked)}
                      onClick={() =>
                        update.mutate({
                          id: item.id,
                          active: !item.active,
                          expectedVersion: item.version,
                        } as never)
                      }
                    >
                      {item.active ? <Archive /> : <ArchiveRestore />}
                    </Button>
                  </div>
                )}
              </li>
            )
          })}
        </ul>
      )}
      {archivedCount > 0 && (
        <label className="flex items-center gap-2 text-sm text-muted-foreground">
          <Switch checked={showArchived} onCheckedChange={setShowArchived} />
          Show archived ({archivedCount})
        </label>
      )}

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing?.item ? `Edit ${noun}` : `Add ${noun}`}</DialogTitle>
            <DialogDescription>
              Changes apply to new quotes. Existing quotes keep what they were approved with.
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault()
              submit()
            }}
          >
            {rendered?.fields}
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="outline"
                className="h-10"
                onClick={() => setEditing(null)}
              >
                Cancel
              </Button>
              <Button type="submit" className="h-10" disabled={hasErrors || saving}>
                {saving && <Loader2 className="animate-spin" />}
                {editing?.item ? 'Save changes' : `Add ${noun}`}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SettingsCard>
  )
}
